<template>
  <div class="device-management">
    <NavBar />
    <h1 >text for space</h1>
    <h1 >text for space</h1>
    
    <div class="container-fluid py-4">
      <!-- Header -->
      <div class="row mb-4">
        <div class="col">
          <h1 class="h3 mb-0 text-gray-800">Device Management</h1>
          <p class="text-muted">Monitor user device activity and detect credential sharing</p>
        </div>
        <div class="col-auto">
          <b-button
            variant="primary"
            @click="refreshData"
            :disabled="loading"
          >
            <b-spinner small v-if="loading" class="mr-2" />
            <i v-else class="fas fa-sync-alt mr-2" />
            Refresh
          </b-button>
        </div>
      </div>

      <!-- Filter Controls -->
      <div class="row mb-4">
        <div class="col-md-8">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title mb-3">Filters</h5>
              <div class="row">
                <div class="col-md-4">
                  <label for="minDevices" class="form-label">Minimum Devices</label>
                  <b-form-select
                    id="minDevices"
                    v-model="filters.minDevices"
                    :options="deviceOptions"
                    @change="applyFilters"
                  />
                </div>
                <div class="col-md-4">
                  <label for="daysBack" class="form-label">Days Back</label>
                  <b-form-select
                    id="daysBack"
                    v-model="filters.daysBack"
                    :options="daysOptions"
                    @change="applyFilters"
                  />
                </div>
                <div class="col-md-4">
                  <label for="riskLevel" class="form-label">Risk Level</label>
                  <b-form-select
                    id="riskLevel"
                    v-model="filters.riskLevel"
                    :options="riskOptions"
                    @change="applyFilters"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card">
            <div class="card-body text-center">
              <h5 class="card-title">Quick Stats</h5>
              <div class="row">
                <div class="col-4">
                  <div class="text-danger">
                    <strong>{{ highRiskUsers.length }}</strong>
                    <br>
                    <small>High Risk</small>
                  </div>
                </div>
                <div class="col-4">
                  <div class="text-warning">
                    <strong>{{ mediumRiskUsers.length }}</strong>
                    <br>
                    <small>Medium Risk</small>
                  </div>
                </div>
                <div class="col-4">
                  <div class="text-primary">
                    <strong>{{ suspiciousUsersCount }}</strong>
                    <br>
                    <small>Total</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Alert -->
      <b-alert
        v-if="error"
        variant="danger"
        dismissible
        @dismissed="clearError"
      >
        <i class="fas fa-exclamation-triangle mr-2" />
        {{ error }}
      </b-alert>

      <!-- Suspicious Users Table -->
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">Suspicious Users</h5>
        </div>
        <div class="card-body">
          <div v-if="loading" class="text-center py-4">
            <b-spinner variant="primary" label="Loading..." />
            <p class="mt-2">Loading suspicious users...</p>
          </div>
          
          <div v-else-if="filteredUsers.length === 0" class="text-center py-4">
            <i class="fas fa-shield-alt fa-3x text-success mb-3" />
            <h5>No Suspicious Activity</h5>
            <p class="text-muted">No users found matching the current criteria.</p>
          </div>
          
          <div v-else>
            <b-table
              :items="filteredUsers"
              :fields="tableFields"
              responsive
              striped
              hover
              :per-page="perPage"
              :current-page="currentPage"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
            >
              <!-- Risk Level Badge -->
              <template #cell(risk_level)="row">
                <b-badge
                  :variant="getRiskVariant(row.item.risk_level)"
                  class="px-2 py-1"
                >
                  {{ row.item.risk_level }}
                </b-badge>
              </template>

              <!-- Device Count with Progress -->
              <template #cell(device_count)="row">
                <div class="d-flex align-items-center">
                  <span class="mr-2">{{ row.item.device_count }}</span>
                  <b-progress
                    :value="Math.min((row.item.device_count / 10) * 100, 100)"
                    :variant="getDeviceCountVariant(row.item.device_count)"
                    class="flex-grow-1"
                    height="8px"
                  />
                </div>
              </template>

              <!-- Actions -->
              <template #cell(actions)="row">
                <b-button-group size="sm">
                  <b-button
                    variant="outline-primary"
                    @click="viewUserDevices(row.item)"
                    title="View Devices"
                  >
                    <i class="fas fa-mobile-alt" />
                  </b-button>
                  <b-button
                    variant="outline-info"
                    @click="viewUserAnalysis(row.item)"
                    title="View Analysis"
                  >
                    <i class="fas fa-chart-line" />
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    @click="viewUserProfile(row.item)"
                    title="View Profile"
                  >
                    <i class="fas fa-user" />
                  </b-button>
                </b-button-group>
              </template>
            </b-table>

            <!-- Pagination -->
            <b-pagination
              v-if="filteredUsers.length > perPage"
              v-model="currentPage"
              :total-rows="filteredUsers.length"
              :per-page="perPage"
              align="center"
              class="mt-3"
            />
          </div>
        </div>
      </div>

      <!-- User Devices Modal -->
      <b-modal
        id="userDevicesModal"
        :title="`Devices for ${selectedUser?.email || 'User'}`"
        size="lg"
        hide-footer
      >
        <UserDevicesList
          v-if="selectedUser"
          :user-id="selectedUser.user_id"
          :user-email="selectedUser.email"
        />
      </b-modal>

      <!-- User Analysis Modal -->
      <b-modal
        id="userAnalysisModal"
        :title="`Analysis for ${selectedUser?.email || 'User'}`"
        size="xl"
        hide-footer
      >
        <UserDeviceAnalysis
          v-if="selectedUser"
          :user-id="selectedUser.user_id"
          :user-email="selectedUser.email"
        />
      </b-modal>

      <!-- User Profile Modal -->
      <b-modal
        id="userProfileModal"
        :title="`User Profile - ${selectedUser?.email || 'User'}`"
        size="lg"
        hide-footer
      >
        <div v-if="selectedUser" class="user-profile">
          <!-- User Header -->
          <div class="row mb-4">
            <div class="col-md-8">
              <div class="d-flex align-items-center">
                <div class="user-avatar mr-3">
                  <i class="fas fa-user-circle fa-3x text-primary"></i>
                </div>
                <div>
                  <h4 class="mb-1">{{ selectedUser.username }}</h4>
                  <p class="text-muted mb-0">{{ selectedUser.email }}</p>
                  <b-badge
                    :variant="getRiskVariant(selectedUser.risk_level)"
                    class="mt-1"
                  >
                    {{ selectedUser.risk_level }} Risk
                  </b-badge>
                </div>
              </div>
            </div>
            <div class="col-md-4 text-right">
              <div class="user-stats">
                <div class="stat-item">
                  <strong>{{ selectedUser.device_count }}</strong>
                  <small class="text-muted d-block">Devices</small>
                </div>
              </div>
            </div>
          </div>

          <!-- User Information Cards -->
          <div class="row">
            <!-- Basic Information -->
            <div class="col-md-6 mb-3">
              <div class="card h-100">
                <div class="card-header">
                  <h6 class="mb-0">
                    <i class="fas fa-info-circle mr-2"></i>
                    Basic Information
                  </h6>
                </div>
                <div class="card-body">
                  <div class="info-item mb-2">
                    <strong>User ID:</strong>
                    <span class="ml-2">{{ selectedUser.user_id }}</span>
                  </div>
                  <div class="info-item mb-2">
                    <strong>Username:</strong>
                    <span class="ml-2">{{ selectedUser.username }}</span>
                  </div>
                  <div class="info-item mb-2">
                    <strong>Email:</strong>
                    <span class="ml-2">{{ selectedUser.email }}</span>
                  </div>
                  <div class="info-item mb-2">
                    <strong>Risk Level:</strong>
                    <b-badge
                      :variant="getRiskVariant(selectedUser.risk_level)"
                      class="ml-2"
                    >
                      {{ selectedUser.risk_level }}
                    </b-badge>
                  </div>
                  <div class="info-item">
                    <strong>Reason:</strong>
                    <span class="ml-2 text-muted">{{ selectedUser.reason }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Device Statistics -->
            <div class="col-md-6 mb-3">
              <div class="card h-100">
                <div class="card-header">
                  <h6 class="mb-0">
                    <i class="fas fa-mobile-alt mr-2"></i>
                    Device Statistics
                  </h6>
                </div>
                <div class="card-body">
                  <div class="stat-row mb-3">
                    <div class="d-flex justify-content-between align-items-center">
                      <span>Total Devices:</span>
                      <b-badge variant="primary" class="px-2 py-1">
                        {{ selectedUser.device_count }}
                      </b-badge>
                    </div>
                  </div>
                  <div class="stat-row mb-3">
                    <div class="d-flex justify-content-between align-items-center">
                      <span>Total Logins:</span>
                      <b-badge variant="success" class="px-2 py-1">
                        {{ selectedUser.total_logins }}
                      </b-badge>
                    </div>
                  </div>
                  <div class="stat-row mb-3">
                    <div class="d-flex justify-content-between align-items-center">
                      <span>Avg Logins/Device:</span>
                      <b-badge variant="info" class="px-2 py-1">
                        {{ Math.round(selectedUser.total_logins / selectedUser.device_count) }}
                      </b-badge>
                    </div>
                  </div>
                  <div class="progress mb-2" style="height: 10px;">
                    <div
                      class="progress-bar"
                      :class="'bg-' + getDeviceCountVariant(selectedUser.device_count)"
                      :style="{ width: Math.min((selectedUser.device_count / 10) * 100, 100) + '%' }"
                    >
                    </div>
                  </div>
                  <small class="text-muted">Device usage intensity</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Actions -->
          <div class="row mt-3">
            <div class="col">
              <div class="card">
                <div class="card-header">
                  <h6 class="mb-0">
                    <i class="fas fa-tools mr-2"></i>
                    Quick Actions
                  </h6>
                </div>
                <div class="card-body">
                  <div class="btn-group w-100" role="group">
                    <b-button
                      variant="outline-primary"
                      @click="viewUserDevicesFromProfile"
                      class="flex-fill"
                    >
                      <i class="fas fa-mobile-alt mr-2"></i>
                      View Devices
                    </b-button>
                    <b-button
                      variant="outline-info"
                      @click="viewUserAnalysisFromProfile"
                      class="flex-fill"
                    >
                      <i class="fas fa-chart-line mr-2"></i>
                      View Analysis
                    </b-button>
                    <b-button
                      variant="outline-warning"
                      @click="contactUser"
                      class="flex-fill"
                    >
                      <i class="fas fa-envelope mr-2"></i>
                      Contact User
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import NavBar from '@/components/NavBar.vue';
import UserDevicesList from '@/components/DeviceTracking/UserDevicesList';
import UserDeviceAnalysis from '@/components/DeviceTracking/UserDeviceAnalysis';

export default {
  name: 'DeviceManagement',
  components: {
    NavBar,
    UserDevicesList,
    UserDeviceAnalysis
  },
  data() {
    return {
      filters: {
        minDevices: 4,
        daysBack: 7,
        riskLevel: 'all'
      },
      selectedUser: null,
      currentPage: 1,
      perPage: 10,
      sortBy: 'device_count',
      sortDesc: true,
      deviceOptions: [
        { value: 3, text: '3+ devices' },
        { value: 4, text: '4+ devices' },
        { value: 5, text: '5+ devices' },
        { value: 6, text: '6+ devices' },
        { value: 10, text: '10+ devices' }
      ],
      daysOptions: [
        { value: 1, text: 'Last 24 hours' },
        { value: 3, text: 'Last 3 days' },
        { value: 7, text: 'Last week' },
        { value: 14, text: 'Last 2 weeks' },
        { value: 30, text: 'Last month' }
      ],
      riskOptions: [
        { value: 'all', text: 'All Risk Levels' },
        { value: 'HIGH', text: 'High Risk Only' },
        { value: 'MEDIUM', text: 'Medium Risk Only' },
        { value: 'LOW', text: 'Low Risk Only' }
      ],
      tableFields: [
        {
          key: 'email',
          label: 'Email',
          sortable: true,
          thClass: 'border-0',
          tdClass: 'align-middle'
        },
        {
          key: 'username',
          label: 'Username',
          sortable: true,
          thClass: 'border-0',
          tdClass: 'align-middle'
        },
        {
          key: 'device_count',
          label: 'Devices',
          sortable: true,
          thClass: 'border-0',
          tdClass: 'align-middle'
        },
        {
          key: 'total_logins',
          label: 'Total Logins',
          sortable: true,
          thClass: 'border-0',
          tdClass: 'align-middle'
        },
        {
          key: 'risk_level',
          label: 'Risk Level',
          sortable: true,
          thClass: 'border-0',
          tdClass: 'align-middle'
        },
        {
          key: 'actions',
          label: 'Actions',
          thClass: 'border-0',
          tdClass: 'align-middle'
        }
      ]
    };
  },
  computed: {
    ...mapGetters('deviceTracking', [
      'suspiciousUsers',
      'suspiciousUsersCount',
      'highRiskUsers',
      'mediumRiskUsers',
      'loading',
      'error'
    ]),
    filteredUsers() {
      let users = this.suspiciousUsers;
      
      if (this.filters.riskLevel !== 'all') {
        users = users.filter(user => user.risk_level === this.filters.riskLevel);
      }
      
      return users;
    }
  },
  methods: {
    ...mapActions('deviceTracking', [
      'fetchSuspiciousUsers',
      'clearData'
    ]),
    async refreshData() {
      await this.fetchSuspiciousUsers({
        minDevices: this.filters.minDevices,
        daysBack: this.filters.daysBack
      });
    },
    async applyFilters() {
      this.currentPage = 1;
      await this.refreshData();
    },
    getRiskVariant(riskLevel) {
      switch (riskLevel) {
        case 'HIGH': return 'danger';
        case 'MEDIUM': return 'warning';
        case 'LOW': return 'success';
        default: return 'secondary';
      }
    },
    getDeviceCountVariant(count) {
      if (count >= 6) return 'danger';
      if (count >= 4) return 'warning';
      return 'success';
    },
    viewUserDevices(user) {
      this.selectedUser = user;
      this.$bvModal.show('userDevicesModal');
    },
    viewUserAnalysis(user) {
      this.selectedUser = user;
      this.$bvModal.show('userAnalysisModal');
    },
    viewUserProfile(user) {
      this.selectedUser = user;
      this.$bvModal.show('userProfileModal');
    },
    viewUserDevicesFromProfile() {
      this.$bvModal.hide('userProfileModal');
      this.$bvModal.show('userDevicesModal');
    },
    viewUserAnalysisFromProfile() {
      this.$bvModal.hide('userProfileModal');
      this.$bvModal.show('userAnalysisModal');
    },
    contactUser() {
      if (this.selectedUser) {
        // Create mailto link with user's email
        const subject = encodeURIComponent(`Regarding your TripPlanner account security`);
        const body = encodeURIComponent(`Hello ${this.selectedUser.username},\n\nWe noticed some unusual device activity on your account and wanted to reach out to ensure your account security.\n\nBest regards,\nTripPlanner Security Team`);
        const mailtoLink = `mailto:${this.selectedUser.email}?subject=${subject}&body=${body}`;
        
        // Open default email client
        window.open(mailtoLink);
      }
    },
    clearError() {
      // Clear error from store
    }
  },
  async created() {
    await this.refreshData();
  },
  beforeDestroy() {
    this.clearData();
  }
};
</script>

<style scoped>
.device-management {
  background-color: #f8f9fc;
  min-height: 100vh;
}

.card {
  border: 0;
  box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
}

.card-header {
  background-color: #f8f9fc;
  border-bottom: 1px solid #e3e6f0;
}

.table th {
  border-top: none;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  color: #858796;
}

.badge {
  font-size: 0.75rem;
}

.btn-group-sm > .btn {
  padding: 0.25rem 0.5rem;
}

.progress {
  background-color: rgba(0, 0, 0, 0.1);
}

/* User Profile Modal Styles */
.user-profile .user-avatar {
  text-align: center;
}

.user-profile .stat-item {
  text-align: center;
  padding: 1rem;
  background-color: #f8f9fc;
  border-radius: 0.5rem;
}

.user-profile .info-item {
  padding: 0.25rem 0;
  border-bottom: 1px solid #e3e6f0;
}

.user-profile .info-item:last-child {
  border-bottom: none;
}

.user-profile .stat-row {
  padding: 0.25rem 0;
}

.user-profile .card {
  border: 0;
  box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
}

.user-profile .card-header {
  background-color: #f8f9fc;
  border-bottom: 1px solid #e3e6f0;
  font-weight: 600;
}

.user-profile .btn-group .btn {
  border-radius: 0;
}

.user-profile .btn-group .btn:first-child {
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
}

.user-profile .btn-group .btn:last-child {
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}

@media (max-width: 768px) {
  .container-fluid {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  .card-body {
    padding: 1rem;
  }

  .user-profile .btn-group {
    flex-direction: column;
  }

  .user-profile .btn-group .btn {
    border-radius: 0.375rem !important;
    margin-bottom: 0.5rem;
  }

  .user-profile .btn-group .btn:last-child {
    margin-bottom: 0;
  }
}
</style> 